import { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Urls } from "../../../../Urls";
import axios from "../../../../Axios/Axios";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";

const getMissingFields = (formData) => {
  const missing = [];
  if (!formData.origin) missing.push("origin");
  if (!formData.destination) missing.push("destination");
  if (!formData.truck_types?.value) missing.push("truck_types");
  if (!formData.due_date) missing.push("due_date");
  return missing;
};

const buildLanePayload = (lane, formData) => ({
  ...lane,
  origin: formData.origin,
  destination: formData.destination,
  truck_required: formData.truck_required || "1",
  due_date: formData.due_date,
  truck_types: formData.truck_types || {},
  contract_tenure: [formData.contract_tenure],
  tonnage: formData.capacity,
  tonnage_unit: formData.capacity_unit,
  product_types: formData.product_types,
  additional_details: formData.additional_details,
});

const buildEnquiryLaneDetails = (eld, lanePayload, formData) => ({
  module_key: eld.module_key || "lorri_spot",
  lane_details: lanePayload,
  benchmark_transporter_list: formData.benchmark_transporter_list || [],
  terms_and_condition: eld.terms_and_condition || {},
});

const buildFormDataFromLane = (
  lane,
  missingFields = [],
  benchmarkList = [],
) => ({
  origin: lane.origin || null,
  destination: lane.destination || null,
  truck_types: lane.truck_types?.value
    ? lane.truck_types
    : lane.truck_types?.[0] || {},
  due_date: lane?.due_date || "",
  truck_required: lane.truck_required || "1",
  contract_tenure: lane.contract_tenure?.[0] || {
    label: "Spot Rates",
    value: "Spot Rates",
  },
  benchmark_transporter_list: benchmarkList,
  capacity: lane.tonnage || lane.capacity || "",
  capacity_unit: lane.tonnage_unit || lane.capacity_unit || "MT",
  product_types: lane.product_types || [],
  additional_details: lane.additional_details || "",
});

const useDraftDetail = (draft_id) => {
  const { companyProfile } = useContext(UserApiContext);
  const history = useHistory();

  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: draft,
    isLoading: isDraftLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["draftDetail", draft_id],
    queryFn: async () => {
      const res = await axios.get(`${Urls.spotAgent}/${draft_id}`);
      return res.data;
    },
    enabled: !!draft_id,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const lane = data?.enquiry_lane_details?.lane_details;
      if (lane && !formData) {
        setFormData(
          buildFormDataFromLane(
            lane,
            data.missing_fields || [],
            data.enquiry_lane_details?.benchmark_transporter_list || [],
          ),
        );
      }
    },
  });

  // ── Truck types ────────────────────────────────────────────
  const { data: truckTypes = [] } = useQuery({
    queryKey: ["truckTypes"],
    queryFn: async () => {
      const res = await axios.get(
        `${Urls.Autocomplete.Get.getlist}?searchFields=truck&include_company_trucks=true`,
      );
      const myTrucks = [];
      const otherTrucks = [];
      res.data?.forEach((item) => {
        if (!item.name) return;
        const option = {
          label: item.from_truck_master ? (
            <span>
              <i
                className="fa fa-truck"
                style={{ marginRight: "8px", color: "#393185" }}
              ></i>
              {item.name}
            </span>
          ) : (
            item.name
          ),
          value: item.name,
          truck_types_group: item.network_truck_type_group,
          isMyTruck: !!item.from_truck_master,
        };
        if (item.from_truck_master) myTrucks.push(option);
        else otherTrucks.push(option);
      });
      return [
        { label: "My trucks", options: myTrucks },
        { label: "Other Trucks", options: otherTrucks },
      ];
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Material categories ────────────────────────────────────
  const { data: materialCategories = [] } = useQuery({
    queryKey: ["materialCategories", companyProfile?.domain_data?.company_id],
    queryFn: async () => {
      const companyId = companyProfile?.domain_data?.company_id;
      if (!companyId) return [];
      const { data } = await axios.get(
        `${Urls.spot}?field=get_material_types&company_id=${companyId}`,
      );
      return data?.length > 0 ? data : [];
    },
    enabled: !!companyProfile?.domain_data?.company_id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Fallback to draft data when formData state has not been set yet (onSuccess removed in RQ v5)
  const resolvedFormData =
    formData ??
    (draft?.enquiry_lane_details?.lane_details
      ? buildFormDataFromLane(
          draft.enquiry_lane_details.lane_details,
          draft.missing_fields || [],
          draft.enquiry_lane_details.benchmark_transporter_list || [],
        )
      : null);

  // ── Handlers ───────────────────────────────────────────────
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...(prev ?? resolvedFormData), [field]: value }));
  };

  const handleBack = () => history.push("/trucks/drafts");

  const handleSave = async () => {
    if (!draft || !resolvedFormData) return;
    const lanePayload = buildLanePayload(
      draft.enquiry_lane_details.lane_details,
      resolvedFormData,
    );
    const enquiryLaneDetails = buildEnquiryLaneDetails(
      draft.enquiry_lane_details,
      lanePayload,
      resolvedFormData,
    );
    setIsSubmitting(true);
    try {
      await axios.put(`${Urls.spotAgent}/${draft_id}`, {
        enquiry_lane_details: enquiryLaneDetails,
      });
      refetch();
      toast.success("Changes saved successfully.");
    } catch {
      toast.error("Failed to save changes.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccept = async () => {
    if (!draft || !resolvedFormData) return;
    const missing = getMissingFields(resolvedFormData);
    if (missing.length > 0) {
      toast.error(`Cannot accept — missing: ${missing.join(", ")}`);
      return;
    }
    const lanePayload = buildLanePayload(
      draft.enquiry_lane_details.lane_details,
      resolvedFormData,
    );
    const enquiryLaneDetails = buildEnquiryLaneDetails(
      draft.enquiry_lane_details,
      lanePayload,
      resolvedFormData,
    );
    setIsSubmitting(true);

    const payload = [{ draft_id, enquiry_lane_details: enquiryLaneDetails }];

    try {
      const { data } = await axios.post(
        `${Urls.spotAgent}/accept_draft`,
        payload,
      );
      if (data?.status === "success") {
        toast.success("Enquiry submitted successfully.");
        history.push("/trucks/drafts");
      } else {
        toast.error("Failed to accept draft. Please try again.");
      }
    } catch {
      toast.error("Failed to accept draft. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await axios.delete(`${Urls.spotAgent}/draft_spots`, {
        data: { draft_ids: [draft_id] },
      });
      history.push("/trucks/drafts");
    } catch {
      toast.error("Failed to reject draft.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    draft,
    formData: resolvedFormData,
    truckTypes,
    materialCategories,
    isLoading: isDraftLoading || isSubmitting,
    isError,
    handleFormChange,
    handleBack,
    handleSave,
    handleAccept,
    handleReject,
  };
};

export default useDraftDetail;
